/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Patients Edit/View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppCardLayout from '@nephro/components/AppCardLayout';
import { Input, Form, Row, Col, Select } from 'antd';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  getLastItemUrl,
  bottomOptionsCreateEdit,
  bottomOptionsRead,
  onGetItemById
} from '@nephro/commons/helpers/utils';
import { onKeepOnlyNumbers } from '@nephro/commons/helpers/string-helper';

// Styles
import { getStorageItem } from 'commons/helpers/storage-helper';
import styles from './styles.module.css';

let patientData = {};
const { Option } = Select;

/**
 * Patients View/Edit
 * @param { Object } patientActions - Patient Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const PatientsViewEdit = ({ patientActions, actions }) => {
  const history = useHistory();
  const location = useLocation();
  const { t: translate } = useTranslation();

  const isView = location.pathname.includes('view');

  const [isReadOnly] = React.useState(isView);
  const [patientInfo, setPatientInfo] = React.useState(null);
  const [patientId] = React.useState(
    parseInt(getLastItemUrl(location.pathname), 10)
  );
  const [identificationTypesList, setIdentificationTypesList] =
    React.useState(null);
  const [patientStatusList, setPatientStatusList] = React.useState(null);

  const {
    getPatientById,
    updatePatient,
    getIdentificationTypesArray,
    getPatientStatusArray
  } = patientActions;
  const { setLoading } = actions;

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get and set patient info
  const onGetPatientInfo = (_, response) => {
    if (response?.data) {
      patientData = {
        ...response?.data,
        docType: response?.data?.identification_type?.identification_type_id,
        patient_status: response?.data?.patient_status?.id_patient_status
      };
      setPatientInfo(response?.data, setLoading(false));
    } else {
      onGoBack();
    }
  };

  // Function to get and set identification types list
  const onGetIdentificationTypesList = (_, response) => {
    if (response?.data?.content) {
      setIdentificationTypesList(response.data);
    }
  };

  // Function to get and set patient status list
  const onGetPatientStatusList = (_, response) => {
    if (response?.data) {
      setPatientStatusList(response.data);
    }
  };

  React.useEffect(() => {
    getPatientStatusArray(onGetPatientStatusList);
    getPatientById({ id: patientId }, onGetPatientInfo);
    getIdentificationTypesArray({ paged: false }, onGetIdentificationTypesList);
  }, []);

  // Function to get on submit form values
  const onFinish = (values) => {
    const identification_type = onGetItemById(
      identificationTypesList?.content,
      'identification_type_id',
      values.docType
    );
    const patient_status = onGetItemById(
      patientStatusList,
      'id_patient_status',
      values.patient_status
    );

    const data = {
      ...values,
      patient_class: patientInfo.patient_class,
      id_patient: patientId,
      identification_type,
      patient_status
    };
    updatePatient({ ...data, id: patientId }, onGoBack);
  };

  const onGetStatusItems = () =>
    patientInfo?.patient_status?.description !== 'activo'
      ? patientStatusList
      : patientStatusList.filter(
          (itm) =>
            itm.description === 'inactivo' || itm.description === 'activo'
        );

  const isAdmin = () => {
    const userInfo = getStorageItem('user');
    return userInfo?.role?.name !== 'Admin';
  };

  // Form item list
  const formItemList = [
    {
      item: <Input disabled />,
      name: ['first_name'],
      label: translate('patientMg.name'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['last_name'],
      label: translate('patientMg.lastName'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input readOnly={isReadOnly} />,
      name: ['email'],
      label: translate('patientMg.email'),
      rules: [
        {
          required: true,
          type: 'email'
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input readOnly={isReadOnly} />,
      name: ['contact_phone_number'],
      label: translate('patientMg.phoneNumber'),
      normalize: onKeepOnlyNumbers,
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Row gutter={10} className={styles.mBottom}>
          <Col span={10}>
            <Form.Item
              rules={[
                { required: true, message: translate('patientMg.typeRequired') }
              ]}
              className={styles.mBottom}
              name={['docType']}>
              <Select
                disabled
                showSearch
                placeholder={translate('patientMg.type')}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }>
                {identificationTypesList?.content?.map((item, index) => (
                  <Option key={index} value={item.identification_type_id}>
                    {item.code}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={14}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: translate('patientMg.numRequired')
                }
              ]}
              normalize={onKeepOnlyNumbers}
              className={styles.mBottom}
              name={['id_document']}>
              <Input disabled placeholder={translate('patientMg.number')} />
            </Form.Item>
          </Col>
        </Row>
      ),
      label: translate('patientMg.document'),
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Select
          showSearch
          placeholder={translate('patientMg.status')}
          optionFilterProp="children"
          disabled={isAdmin()}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {onGetStatusItems()?.map((item, index) => (
            <Option key={index} value={item.id_patient_status}>
              {translate(`patientMg.${item.description}`)}
            </Option>
          ))}
        </Select>
      ),
      name: ['patient_status'],
      label: `${translate('patientMg.status')} ${translate('login.patient')}`,
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    }
  ];

  return (
    <AppCardLayout>
      <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
        {patientInfo && (
          <FormGenerator
            initialValues={patientData}
            onFinish={onFinish}
            items={formItemList}
            bottomOptions={
              !isReadOnly
                ? bottomOptionsCreateEdit(
                    onGoBack,
                    translate,
                    'edit',
                    styles.actionButton
                  )
                : bottomOptionsRead(onGoBack, translate, styles.actionButton)
            }
          />
        )}
      </div>
    </AppCardLayout>
  );
};

/**
 * Component definition of proptypes.
 */
PatientsViewEdit.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    createPatient: PropTypes.func.isRequired,
    getPatientById: PropTypes.func.isRequired,
    updatePatient: PropTypes.func.isRequired,
    getIdentificationTypesArray: PropTypes.func.isRequired,
    getPatientStatusArray: PropTypes.func.isRequired
  }).isRequired
};

export default PatientsViewEdit;
